import { useLocation } from 'react-router-dom';

import { BadgeCircle } from '@backrs_ui/common-ui';

import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  openMobileFab,
  setQueuedPrompt,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState';

import { Button, SxProps, Theme } from '@mui/material';

type LubavButtonSize = 'sm' | 'md';

type CommonProps = Readonly<{
  id: string;
  label: string;
  chatMessage?: string;
  buttonSize?: LubavButtonSize;
  allowTools?: boolean;
  useGeneralKnowledge?: boolean;
  gray?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  sx?: SxProps<Theme>;
}>;

type LubavButtonProps = CommonProps &
  (
    | Readonly<{ prompt: string; promptButtonId?: string }>
    | Readonly<{ prompt?: string; promptButtonId: string }>
  );

const NO_TOOLS = 'Do not use tools.';
const PAGE_CONTEXT_ONLY = 'Answer based only on the page context.';
const GENERAL_KNOWLEDGE = 'Use both the page context and your general knowledge to answer.';

export const LubavButton = ({
  id,
  label,
  chatMessage,
  prompt,
  promptButtonId,
  buttonSize = 'md',
  allowTools = true,
  useGeneralKnowledge = false,
  gray = false,
  disabled = false,
  onClick,
  sx: sxProp,
}: LubavButtonProps) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isUserOrGuest } = useAuth();
  const hasQueuedPrompt = useAppSelector((state) => !!state.sessionState.queuedPrompt);

  // A guest can chat, so only a viewer with no identity at all has nowhere to send the prompt.
  if (location.pathname.startsWith('/shared/') && !isUserOrGuest) return null;

  const suffixes: string[] = [];

  if (useGeneralKnowledge) suffixes.push(GENERAL_KNOWLEDGE);
  else if (!allowTools) suffixes.push(PAGE_CONTEXT_ONLY);

  if (!allowTools) suffixes.push(NO_TOOLS);

  const basePrompt = prompt ?? '';
  const fullPrompt = suffixes.length > 0 ? `${basePrompt}\n\n${suffixes.join(' ')}` : basePrompt;

  return (
    <Button
      id={id}
      hierarchy="Secondary gray"
      size="sm"
      icon="Leading"
      disabled={disabled}
      startIcon={<BadgeCircle size="md" gray={gray} />}
      sx={[buttonSize === 'sm' && smButtonSx, ...(Array.isArray(sxProp) ? sxProp : [sxProp])]}
      onClick={() => {
        if (hasQueuedPrompt) return;

        dispatch(
          setQueuedPrompt({
            label,
            chatMessage,
            prompt: fullPrompt,
            timestamp: Date.now(),
            promptButtonId,
          }),
        );
        dispatch(openMobileFab());
        onClick?.();
      }}
    >
      {label}
    </Button>
  );
};

const smButtonSx: SxProps<Theme> = ({ backrs }) => ({
  py: backrs.spacing['spacing-xxs'],
  px: backrs.spacing['spacing-sm'],
  fontWeight: 400,
  minHeight: 0,
  borderWidth: '1px',
});
